'use client';

import { useState } from 'react';
import ReviewModal from '@/components/sections/builder/elements/body/customer/customer_review/form/modal-customer-review';
import { MyReviewElementDataType } from '@/components/sections/builder/elements/body/customer/customer_review/type';
import { StoreCustomerUnreviewedProduct } from '@/types/http';
import { usePaginationSearchParams } from '@/hooks/use-pagination';
import { useCustomerCreateReview, useListUnReview } from '@/hooks/api/storefront/customer';
import Cookies from 'js-cookie';
import Image from 'next/image';
import Pagination from '@/components/commons/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import useModal from '@/hooks/useModal';
import * as Yup from 'yup';
import { toast } from '@/components/ui/use-toast';
import { Formik, FormikProps } from 'formik';
import { HttpTypes } from '@/types';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { dummyUnReviewItems } from '@/constant/dummyData/dummy-customer-review';
import { useStoreSetting } from '@/app/(store)/s/[domain]/store-context';

type listUnReviewProps = {
  content: MyReviewElementDataType;
  preview?: boolean;
};

interface ReviewFormValues {
  rating: number;
  comment: string;
  media_files: Array<{
    id: string;
    thumbnail: string;
    file: string;
  }>;
}

const ReviewSchema = Yup.object().shape({
  rating: Yup.number()
    .min(1, 'Minimum rating 1 star')
    .max(5, 'Maximum rating 5 stars')
    .required('Rating is required'),
  comment: Yup.string().required('Review is required').max(1000, 'Review maximum 1000 characters'),
  media_files: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required(),
      thumbnail: Yup.string().url().required(),
      file: Yup.string().url().required(),
    })
  ),
});

const generateUniqueId = () => {
  return Math.random().toString(36).substr(2, 9);
};

export default function ListUnReview({ content, preview }: listUnReviewProps) {
  const token = Cookies.get('accessToken');
  const [params, setParams] = usePaginationSearchParams();
  const { storeSetting } = useStoreSetting();

  const {
    data: unReviewData,
    isLoading,
    refetch,
  } = useListUnReview(
    token as string,
    {
      page: params.pageIndex || 1,
      pageSize: params.pageSize || 10,
    },
    {
      enabled: !preview,
    }
  );

  const displayData = preview
    ? { data: dummyUnReviewItems, totalCount: dummyUnReviewItems.length }
    : unReviewData;

  const isLoadingData = preview ? false : isLoading;

  const modalAddReview = useModal();

  const [selectedProduct, setSelectedProduct] = useState<StoreCustomerUnreviewedProduct | null>(
    null
  );

  const createReviewMutation = useCustomerCreateReview(token as string, {
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Thank you. Your review has been received and will be moderated shortly.',
      });
      if (!preview) {
        refetch();
      }
      closeReviewModal();
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Failed',
        description: error?.message || 'Failed Add Review',
      });
    },
  });

  const initialValues: ReviewFormValues = {
    rating: 0,
    comment: '',
    media_files: [],
  };

  const openReviewModal = (product: StoreCustomerUnreviewedProduct) => {
    if (preview) {
      toast({
        title: 'Preview Mode',
        description: 'This is a preview. Reviews cannot be submitted in preview mode.',
      });
      return;
    }

    setSelectedProduct(product);
    setTimeout(() => {
      modalAddReview.open();
    }, 100);
  };

  const closeReviewModal = () => {
    modalAddReview.setIsShow(false);
    setSelectedProduct(null);
  };

  const handleSubmitReview = async (values: ReviewFormValues) => {
    if (!selectedProduct || preview) return;

    try {
      const attachments = values.media_files.map((file) => {
        const isVideo =
          /\.(mp4|webm|ogg|mov|avi|mkv|flv|wmv)$/i.test(file.file) || file.file.includes('video');

        return {
          url: file.file,
          type: isVideo ? ('video' as const) : ('image' as const),
        };
      });

      const payload: HttpTypes.StoreCustomerReviewCreatePayload = {
        order_id: selectedProduct.order_id,
        product_item_id: selectedProduct.item_id,
        rating: values.rating,
        comment: values.comment,
        attachments: attachments.length > 0 ? attachments : undefined,
      };

      if (
        storeSetting?.product_settings?.auto_approve === true &&
        payload.rating >= storeSetting?.product_settings.auto_approve_review_threshold
      ) {
        payload.status = 'approved';
      } else {
        payload.status = 'pending';
      }

      await createReviewMutation.mutateAsync(payload);
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Error',
          description: error.message || 'Something went wrong.',
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div className='mx-auto w-full p-6'>
      <div className='space-y-4'>
        {isLoadingData ? (
          <>
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className='flex items-start gap-4'>
                <Skeleton className='h-16 w-16 flex-shrink-0' />
                <div className='flex-1 space-y-2'>
                  <Skeleton className='h-5 w-3/4' />
                  <Skeleton className='h-4 w-1/2' />
                  <Skeleton className='h-4 w-2/3' />
                  <div className='flex items-center justify-between'>
                    <Skeleton className='h-3 w-32' />
                    <Skeleton className='h-8 w-24' />
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {!displayData || displayData.data.length === 0 ? (
              <div className='flex justify-center py-8'>
                <div
                  className='text-gray-500'
                  style={{ color: content?.colorScheme?.textSecondary }}
                >
                  No products to review
                </div>
              </div>
            ) : (
              <>
                {displayData.data.map((item, index) => {
                  const isLastItem = index === displayData.data.length - 1;

                  return (
                    <div key={item.item_id}>
                      <div className='flex items-start gap-4'>
                        <div className='h-16 w-16 flex-shrink-0 overflow-hidden'>
                          {item.product_images?.[0] && (
                            <Image
                              src={item.product_images[0]}
                              alt={item.product_name || ''}
                              width={64}
                              height={64}
                              className='h-full w-full object-cover'
                            />
                          )}
                        </div>
                        <div className='flex-1'>
                          <h3
                            className='mb-1 text-base font-medium text-gray-900'
                            style={{ color: content?.colorScheme?.text }}
                          >
                            {item.product_name}
                          </h3>
                          <p
                            className='mb-2 text-sm text-gray-600'
                            style={{ color: content?.colorScheme?.textSecondary }}
                          >
                            {item.item_name}
                          </p>
                          <div className='flex items-center justify-between'>
                            <p className='text-xs text-muted-foreground'>
                              Order Date:{' '}
                              {format(new Date(item.order_created_at ?? ''), 'dd MMMM yyyy')}
                            </p>
                            <Button
                              style={{
                                backgroundColor: content?.colorScheme?.btnSolidBackgroud,
                                color: content?.colorScheme?.btnSolidlabel,
                              }}
                              onClick={() => openReviewModal(item)}
                            >
                              Write a review
                            </Button>
                          </div>
                        </div>
                      </div>
                      {!isLastItem && <Separator className='my-4 border-gray-200' />}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      <div className='mt-6'>
        <Pagination
          currentPage={params.pageIndex || 1}
          pageSize={params.pageSize || 10}
          totalCount={displayData?.totalCount || 0}
          onPageChange={(page) => setParams((prev) => ({ ...prev, pageIndex: page }))}
          onPageSizeChange={(pageSize) =>
            setParams((prev) => ({ ...prev, pageSize, pageIndex: 1 }))
          }
        />
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={ReviewSchema}
        onSubmit={handleSubmitReview}
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
      >
        {(formikProps: FormikProps<ReviewFormValues>) => (
          <ReviewModal
            content={content}
            openModal={modalAddReview.isShow}
            setOpenModal={(open) => {
              if (!open) {
                closeReviewModal();
              }
            }}
            selectedProduct={selectedProduct}
            formikProps={formikProps}
            isLoading={createReviewMutation.isPending}
            generateUniqueId={generateUniqueId}
          />
        )}
      </Formik>
    </div>
  );
}
